import React, { createContext, useState, useEffect, useContext, useMemo } from "react";
import { Box, CircularProgress } from '@mui/material';
import { useAuthenticationContext } from "../../global/hooks/useAuthenticationContext";
import { useAuthorizationContext } from "../../global/hooks/useAuthorizationContext";
import { useErrorContext } from "../../global/hooks/useErrorContext";
import { ICphContextType } from "../types/interfaces/ICphContextType";
import { ICphProviderProps } from "../types/interfaces/ICphProviderProps";
import { IIssue } from "../types/interfaces/IIssue";
import { getKeyFromRoute } from "../../global/utilities/ProcessQueryString";
import { useApi } from "../hooks/useApi";
import { getUri_GetAll, getUri_GetPriorities } from "../services/ApiUriService";
import { IPriority } from "../types/interfaces/IPriority";

export const CphContext = createContext<ICphContextType | undefined>(undefined);

export const useCphContext = (): ICphContextType => {
    const context = useContext(CphContext);
    if (!context) {
        throw new Error('useCphContext must be used within a CphProvider');
    }
    return context;
};

export const CphProvider: React.FC<ICphProviderProps> = ({ children }) => {
    const { cstKey } = useAuthenticationContext();
    const { roles } = useAuthorizationContext();
    const [loadingCphCtx, setLoadingCphCtx] = useState<boolean>(true);
    const [customerKey, setCustomerKey] = useState<string | undefined>(undefined);
    const [isUserFlag, setIsUserFlag] = useState<boolean>(false);
    const [isStaffFlag, setIsStaffFlag] = useState<boolean>(false);
    const [isSingleIssueFlag, setIsSingleIssueFlag] = useState<boolean>(false);
    const [singleIssueKey, setSingleIssueKey] = useState<string | undefined>(undefined);
    const [issuesList, setIssuesList] = useState<IIssue[] | null>(null);
    const [issue, setIssue] = useState<IIssue | null>(null);
    const [prioritiesList, setPrioritiesList] = useState<IPriority[] | null>(null);
    const [apiError, setApiError] = useState(false);
    const { setError } = useErrorContext(); //this error will display on UI so use wisely!

    // On mount, set singleIssueKey from query string if present
    useEffect(() => {
        const key = getKeyFromRoute();
        if (key != null) {
            setSingleIssueKey(key);
            setIsSingleIssueFlag(true);
        } else {
            setSingleIssueKey(undefined);
            setIsSingleIssueFlag(false);
        }
    }, []);

    const STAFF_ROLE_NAME: string = process.env.REACT_APP_NFROLE_STAFF!;
    const hasStaffRole = roles?.includes(STAFF_ROLE_NAME) ?? false;

    const setUser = () => {
        setCustomerKey(cstKey);
        setIsStaffFlag(hasStaffRole ?? false);
        setIsUserFlag(!hasStaffRole);
    };

    let getIssuesUri = getUri_GetAll(hasStaffRole, customerKey!);
    const { data: issuesData, loading: issuesLoading, error: issuesError } = useApi<IIssue[] | null>(getIssuesUri, undefined, [], 'GET');
    
    let getPrioritiesUri = getUri_GetPriorities();
    const { data: prioritiesData, loading: prioritiesLoading, error: prioritiesError } = useApi<IPriority[] | null>(getPrioritiesUri, undefined, [], 'GET');

    useEffect(() => {
        setUser();
        if (!issuesLoading && !prioritiesLoading) {
            let issues: IIssue[] | null = issuesData as IIssue[];
            setIssuesList(issues);

            if (isSingleIssueFlag && singleIssueKey) {
                let foundIssue: IIssue | null = null;
                if (Array.isArray(issues)) {
                    foundIssue = issues.find((i) => i.issueKey === singleIssueKey) || null;
                }
                setIssue(foundIssue);
                console.log('found Issue', foundIssue?.issueKey);
            }

            let priorities: IPriority[] | null = prioritiesData as IPriority[];
            setPrioritiesList(priorities);

            setLoadingCphCtx(false);
        }
        if (issuesError || prioritiesError) {
            console.log("Api error in CphUserProvider");
            setApiError(true);
            setError({ message: null, statusCode: 0 });
            setLoadingCphCtx(false);
        }
    }, [issuesLoading, prioritiesLoading, issuesError, prioritiesError, isSingleIssueFlag, singleIssueKey]);

    const value = useMemo(() => ({
        loadingCphCtx,
        customerKey,
        isUser: isUserFlag,
        isStaff: isStaffFlag,
        isSingleIssue: isSingleIssueFlag,
        issue: issue,
        issuesList,
        prioritiesList,
        setIssue,
        setIssuesList,
        setPrioritiesList,
        setIsSingleIssueFlag,
        setSingleIssueKey

    }), [
        loadingCphCtx,
        customerKey,
        isUserFlag,
        isStaffFlag,
        isSingleIssueFlag,
        issue,
        issuesList,
        prioritiesList,
        setIssue,
        setIssuesList,
        setPrioritiesList,
        setIsSingleIssueFlag,
        setSingleIssueKey
    ]);

    return (
        <CphContext.Provider value={value}>
            {loadingCphCtx ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <CircularProgress />
                </Box>
            ) : (
                children
            )}
        </CphContext.Provider>
    );
};
